import React from 'react';
import { createRoot } from 'react-dom/client';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { normalizeBranch, loadLogos, renderStandardHeader, renderStandardFooter } from './pdfCommon';
import LabReport from '../components/LabReport';

// Legacy color tokens kept for any remaining helpers
const COLORS = {
  primary: [26, 58, 92],
  secondary: [44, 184, 160],
  text: [30, 30, 30],
  muted: [100, 110, 120],
  light: [244, 246, 248],
  border: [220, 225, 230],
  danger: [220, 38, 38],
  warning: [217, 119, 6],
  success: [22, 163, 74],
  critical: [127, 29, 29],
};

const STATUS_LABELS = {
  normal: 'Normal',
  high: 'Alto ↑',
  low: 'Bajo ↓',
  critical: '⚠ Crítico',
  unknown: 'Sin ref.',
};

function setColor(doc, arr) { doc.setTextColor(...arr); }
function setFill(doc, arr) { doc.setFillColor(...arr); }
function setDraw(doc, arr) { doc.setDrawColor(...arr); }
function line(doc, x1, y1, x2, y2) { doc.line(x1, y1, x2, y2); }

// ── New PDF generator: renders LabReport component → html2canvas → jsPDF ───
export const generateLabReport = async (reportData) => {
  const { order, result, patient, owner, vet, branch } = reportData;
  const branchNorm = normalizeBranch(branch);
  const logos = await loadLogos();

  const orderNum = order?.orderNumber || result?.orderId?.slice(-8).toUpperCase() || '—';
  const dateStr = result?.reportGeneratedAt
    ? new Date(result.reportGeneratedAt.seconds * 1000).toLocaleDateString('es-CL')
    : new Date().toLocaleDateString('es-CL');
  const headerMeta = { title: 'INFORME DE LABORATORIO', number: orderNum, date: dateStr };

  // 1. Mount LabReport in a hidden off-screen div
  const container = document.createElement('div');
  container.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:794px;background:#fff;z-index:-1;';
  document.body.appendChild(container);

  const root = createRoot(container);
  root.render(
    React.createElement(LabReport, { order, result, patient, owner, vet, branch, mode: 'print' })
  );

  // 2. Wait for React to paint
  await new Promise((r) => setTimeout(r, 450));

  let blob;
  try {
    const element = container.firstElementChild || container;
    const SCALE = 2;

    // 2b. Measure top-level blocks BEFORE capture → candidate page-break positions
    //     (header rows, patient card, altered summary, each exam section,
    //      observations, footer). We only ever break between these blocks so
    //      no table / section / signature block is ever cut in half.
    const rootTop = element.getBoundingClientRect().top;
    const breakPoints = Array.from(element.children)
      .map((child) => Math.round((child.getBoundingClientRect().top - rootTop) * SCALE))
      .filter((v) => v > 0);

    // 3. Capture with html2canvas at 2× for crisp PDF
    const canvas = await html2canvas(element, {
      scale: SCALE,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
      width: 794,
    });

    // 4. Slice canvas into A4 pages, breaking only at block boundaries
    const pdf     = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageW   = pdf.internal.pageSize.getWidth();   // 210 mm
    const pageH   = pdf.internal.pageSize.getHeight();  // 297 mm
    const headerHeight = 30;                            // mm — standard header
    const footerH = 12;                                 // mm — standard footer
    const pxPerMm = canvas.width / pageW;
    const pageContentHpx = Math.floor((pageH - headerHeight - footerH) * pxPerMm);

    let yPx   = 0;
    let pageN = 0;

    while (yPx < canvas.height) {
      if (pageN > 0) pdf.addPage();
      renderStandardHeader(pdf, branchNorm, logos, headerMeta, headerHeight);

      const remaining = canvas.height - yPx;
      let sliceH;
      if (remaining <= pageContentHpx) {
        // Everything left fits on this page (this is the single-page happy path)
        sliceH = remaining;
      } else {
        // Snap the page end to the last block boundary that still fits
        const idealEnd = yPx + pageContentHpx;
        let bp = -1;
        for (const b of breakPoints) {
          if (b > yPx + 4 && b <= idealEnd) bp = Math.max(bp, b);
        }
        // Fallback: if a single block is taller than a page, cut at ideal end
        sliceH = bp > yPx ? bp - yPx : pageContentHpx;
      }

      const slice = document.createElement('canvas');
      slice.width  = canvas.width;
      slice.height = sliceH;
      slice.getContext('2d').drawImage(canvas, 0, yPx, canvas.width, sliceH, 0, 0, canvas.width, sliceH);

      const sliceMmH = (sliceH * pageW) / canvas.width;
      // JPEG (q=0.9) en vez de PNG: mismo raster nítido pero mucho menor tamaño
      pdf.addImage(slice.toDataURL('image/jpeg', 0.9), 'JPEG', 0, headerHeight, pageW, sliceMmH, undefined, 'FAST');

      yPx += sliceH;
      pageN++;
    }

    renderStandardFooter(pdf, branchNorm);

    pdf.setProperties({
      title:   `Informe Lab ${order?.orderNumber || ''}`,
      author:  'HCV UDLA',
      creator: 'Sistema HCV UDLA',
      subject: result?.examCategory || 'Laboratorio',
    });

    blob = pdf.output('blob');
  } finally {
    root.unmount();
    container.remove();
  }

  return blob;
};

// ── Legacy jsPDF helpers (unused — kept as fallback reference) ──────────────
const _legacyGenerateLabReport = async (reportData) => {
  const {
    order, result, patient, owner, vet, clinicName = 'Hospital Clínico Veterinario UDLA',
  } = reportData;

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pw = 210;
  const margin = 18;
  const cw = pw - margin * 2;
  let y = margin;

  // ── HEADER ──────────────────────────────────────────────────────────────────
  setFill(doc, COLORS.primary);
  doc.rect(0, 0, pw, 28, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  setColor(doc, [255, 255, 255]);
  doc.text(clinicName, margin, 11);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text('INFORME DE LABORATORIO CLÍNICO VETERINARIO', margin, 18);

  doc.setFontSize(8);
  const orderNum = order?.orderNumber || result?.orderId?.slice(-8).toUpperCase() || 'N/D';
  const dateStr = result?.reportGeneratedAt
    ? new Date(result.reportGeneratedAt.seconds * 1000).toLocaleDateString('es-CL')
    : new Date().toLocaleDateString('es-CL');
  doc.text(`N° Orden: ${orderNum}   |   Fecha emisión: ${dateStr}`, pw - margin, 11, { align: 'right' });

  y = 36;

  // ── PATIENT / OWNER / VET BLOCK ──────────────────────────────────────────────
  setFill(doc, COLORS.light);
  setDraw(doc, COLORS.border);
  doc.roundedRect(margin, y, cw, 36, 2, 2, 'FD');

  const col1x = margin + 4;
  const col2x = margin + cw / 2 + 4;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  setColor(doc, COLORS.secondary);
  doc.text('DATOS DEL PACIENTE', col1x, y + 6);
  doc.text('SOLICITANTE / PROPIETARIO', col2x, y + 6);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  setColor(doc, COLORS.text);

  const patientName = patient?.name || order?.patientName || 'N/D';
  const species = patient?.species || order?.patientSpecies || 'N/D';
  const breed = patient?.breed || 'N/D';
  const sex = patient?.genero || patient?.sex || 'N/D';
  const bdate = patient?.birthDate || patient?.birthdate || '';
  const weight = patient?.weight ? `${patient.weight} kg` : 'N/D';

  const leftLines = [
    `Nombre: ${patientName}`,
    `Especie: ${species}   Raza: ${breed}`,
    `Sexo: ${sex}   Peso: ${weight}`,
    bdate ? `Fecha nac.: ${bdate}` : '',
  ].filter(Boolean);
  leftLines.forEach((l, i) => doc.text(l, col1x, y + 13 + i * 5.5));

  const vetName = vet?.nombre ? `Dr/a. ${vet.nombre} ${vet.apellidos || ''}` : order?.vetName || 'N/D';
  const ownerName = owner?.name || order?.ownerName || 'N/D';
  const rightLines = [
    `M.V.: ${vetName}`,
    `Propietario: ${ownerName}`,
    owner?.phone ? `Teléfono: ${owner.phone}` : '',
  ].filter(Boolean);
  rightLines.forEach((l, i) => doc.text(l, col2x, y + 13 + i * 5.5));

  y += 42;

  // ── EXAM TITLE ──────────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  setColor(doc, COLORS.primary);
  doc.text(`Examen: ${result?.examTypeName || 'N/D'}`, margin, y);
  y += 7;

  // ── PARAMETERS TABLE ─────────────────────────────────────────────────────────
  const colWidths = [62, 24, 20, 38, 22];
  const headers = ['Parámetro', 'Valor', 'Unidad', 'Rango referencia', 'Estado'];
  const colX = headers.reduce((acc, _, i) => {
    acc.push(i === 0 ? margin : acc[i - 1] + colWidths[i - 1]);
    return acc;
  }, []);
  const rowH = 7;
  const tableH = rowH;

  // Header row
  setFill(doc, COLORS.primary);
  doc.rect(margin, y, cw, tableH, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  setColor(doc, [255, 255, 255]);
  headers.forEach((h, i) => doc.text(h, colX[i] + 2, y + 5));
  y += tableH;

  // Group params by category
  const params = result?.extractedParameters || [];
  const grouped = {};
  params.forEach((p) => {
    const cat = p.category || 'General';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(p);
  });

  doc.setFontSize(8);
  let rowIdx = 0;

  for (const [category, catParams] of Object.entries(grouped)) {
    // Category subheader
    setFill(doc, COLORS.secondary);
    doc.rect(margin, y, cw, 5.5, 'F');
    doc.setFont('helvetica', 'bold');
    setColor(doc, [255, 255, 255]);
    doc.text(category, margin + 2, y + 4);
    y += 5.5;

    for (const param of catParams) {
      if (y > 255) {
        doc.addPage();
        y = margin;
      }

      const isAbnormal = ['high', 'low', 'critical'].includes(param.status);
      const isCritical = param.status === 'critical';
      const bgColor = rowIdx % 2 === 0 ? [255, 255, 255] : [248, 250, 252];

      setFill(doc, bgColor);
      doc.rect(margin, y, cw, rowH, 'F');

      setDraw(doc, COLORS.border);
      doc.rect(margin, y, cw, rowH, 'S');

      const refStr = param.refMin !== null && param.refMax !== null
        ? `${param.refMin} – ${param.refMax}`
        : '—';

      const statusLabel = STATUS_LABELS[param.status] || '—';
      const statusColor = {
        normal: COLORS.success,
        high: COLORS.danger,
        low: COLORS.warning,
        critical: COLORS.critical,
        unknown: COLORS.muted,
      }[param.status] || COLORS.muted;

      if (param.unmatched) {
        doc.setFont('helvetica', 'italic');
        setColor(doc, COLORS.muted);
      } else if (isAbnormal) {
        doc.setFont('helvetica', 'bold');
        setColor(doc, isCritical ? COLORS.critical : COLORS.danger);
      } else {
        doc.setFont('helvetica', 'normal');
        setColor(doc, COLORS.text);
      }
      doc.text(param.paramName || param.name || '—', colX[0] + 2, y + 5);

      if (isAbnormal) {
        doc.setFont('helvetica', 'bold');
        setColor(doc, isCritical ? COLORS.critical : COLORS.danger);
      } else {
        doc.setFont('helvetica', 'normal');
        setColor(doc, COLORS.text);
      }
      doc.text(param.value || '—', colX[1] + 2, y + 5);

      doc.setFont('helvetica', 'normal');
      setColor(doc, COLORS.muted);
      doc.text(param.unit || '—', colX[2] + 2, y + 5);
      doc.text(refStr, colX[3] + 2, y + 5);

      setColor(doc, statusColor);
      doc.setFont('helvetica', 'bold');
      doc.text(statusLabel, colX[4] + 2, y + 5);

      y += rowH;
      rowIdx++;
    }
  }

  if (params.length === 0) {
    setFill(doc, [255, 255, 255]);
    doc.rect(margin, y, cw, 10, 'F');
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    setColor(doc, COLORS.muted);
    doc.text('Sin parámetros registrados.', margin + 2, y + 7);
    y += 10;
  }

  y += 8;

  // ── OBSERVATIONS ─────────────────────────────────────────────────────────────
  if (y > 240) { doc.addPage(); y = margin; }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  setColor(doc, COLORS.primary);
  doc.text('Observaciones / Interpretación:', margin, y);
  y += 4;

  setFill(doc, COLORS.light);
  setDraw(doc, COLORS.border);
  doc.roundedRect(margin, y, cw, 24, 2, 2, 'FD');
  if (result?.observations) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    setColor(doc, COLORS.text);
    const obsLines = doc.splitTextToSize(result.observations, cw - 6);
    obsLines.forEach((l, i) => doc.text(l, margin + 3, y + 6 + i * 5));
  }
  y += 28;

  // ── FOOTER ───────────────────────────────────────────────────────────────────
  const footerY = 282;
  setDraw(doc, COLORS.border);
  line(doc, margin, footerY - 6, pw - margin, footerY - 6);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  setColor(doc, COLORS.muted);
  doc.text(
    `Documento generado el ${new Date().toLocaleString('es-CL')} · ${clinicName}`,
    pw / 2, footerY - 2, { align: 'center' }
  );

  if (result?.reviewedByName) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    setColor(doc, COLORS.primary);
    doc.text(`Técnico revisor: ${result.reviewedByName}`, pw - margin, footerY - 2, { align: 'right' });
  }

  return doc.output('blob');
}; // end _legacyGenerateLabReport

// ── Order PDF generator ───────────────────────────────────────────────────────
export const generateLabOrderPDF = async ({ order, patient, owner, vet, branch, userName }) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pw = 210;
  const margin = 18;
  const cw = pw - margin * 2;
  const branchNorm = normalizeBranch(branch);
  const logos = await loadLogos();

  const headerMeta = {
    title: 'ORDEN DE LABORATORIO',
    number: order?.orderNumber || order?.id?.slice(-8).toUpperCase() || '—',
    date: new Date().toLocaleDateString('es-CL'),
  };
  renderStandardHeader(doc, branchNorm, logos, headerMeta, 30);

  let y = 40;

  // Patient block
  setFill(doc, COLORS.light);
  setDraw(doc, COLORS.border);
  doc.roundedRect(margin, y, cw, 34, 2, 2, 'FD');

  const patientName = patient?.name || order?.patientName || 'N/D';
  const species = patient?.species || order?.patientSpecies || 'N/D';
  const breed = patient?.breed || 'N/D';
  const sex = patient?.genero || patient?.sex || 'N/D';
  const birthDate = patient?.birthDate || patient?.birthdate || '';
  const weight = patient?.weight ? `${patient.weight} kg` : 'N/D';

  const vetName = vet?.nombre ? `Dr/a. ${vet.nombre} ${vet.apellidos || ''}` : order?.vetName || 'N/D';
  const ownerName = owner?.name || order?.ownerName || 'N/D';

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  setColor(doc, COLORS.primary);
  doc.text('DATOS DEL PACIENTE', margin + 4, y + 7);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  setColor(doc, COLORS.text);
  const leftLines = [
    `Nombre: ${patientName}`,
    `Especie: ${species}   Raza: ${breed}`,
    `Sexo: ${sex}   Peso: ${weight}`,
    birthDate ? `Fecha nac.: ${birthDate}` : '',
  ].filter(Boolean);
  leftLines.forEach((l, i) => doc.text(l, margin + 4, y + 14 + i * 5.5));

  doc.setFont('helvetica', 'bold');
  setColor(doc, COLORS.primary);
  doc.text('SOLICITANTE', margin + cw / 2 + 4, y + 7);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  setColor(doc, COLORS.text);
  const rightLines = [
    `M.V.: ${vetName}`,
    `Propietario: ${ownerName}`,
    owner?.phone ? `Teléfono: ${owner.phone}` : '',
  ].filter(Boolean);
  rightLines.forEach((l, i) => doc.text(l, margin + cw / 2 + 4, y + 14 + i * 5.5));
  y += 42;

  // Exams requested
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  setColor(doc, COLORS.primary);
  doc.text('EXÁMENES SOLICITADOS', margin, y);
  y += 10;

  const exams = order?.examsRequested || [];
  if (exams.length === 0) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    setColor(doc, COLORS.muted);
    doc.text('Sin exámenes registrados.', margin, y);
    y += 8;
  } else {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    setColor(doc, [255, 255, 255]);
    setFill(doc, COLORS.primary);
    doc.rect(margin, y, cw, 7, 'F');
    doc.text('Examen', margin + 2, y + 5);
    y += 7;

    doc.setFontSize(9);
    exams.forEach((e, i) => {
      if (y > 250) { doc.addPage(); y = margin; }
      setFill(doc, i % 2 === 0 ? [255, 255, 255] : [248, 250, 252]);
      doc.rect(margin, y, cw, 8, 'F');
      setDraw(doc, COLORS.border);
      doc.rect(margin, y, cw, 8, 'S');
      setColor(doc, COLORS.text);
      doc.setFont('helvetica', 'normal');
      doc.text(e.examTypeName || '—', margin + 2, y + 5.5);
      y += 8;
    });
  }

  y += 10;
  if (order?.motivo) {
    if (y > 250) { doc.addPage(); y = margin; }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    setColor(doc, COLORS.primary);
    doc.text('MOTIVO / DIAGNÓSTICO PRESUNTIVO:', margin, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    setColor(doc, COLORS.text);
    const motiveLines = doc.splitTextToSize(order.motivo, cw);
    motiveLines.forEach((l) => {
      if (y > 270) { doc.addPage(); y = margin; }
      doc.text(l, margin, y);
      y += 5.5;
    });
  }

  renderStandardFooter(doc, branchNorm, { pageN: 1, totalPages: doc.internal.getNumberOfPages() });
  return doc.output('blob');
};
